import { motion } from 'framer-motion';
import {
  Compass,
  Target,
  Heart,
  Lightbulb,
  CheckCircle,
  ArrowRight,
  Rocket,
  Users,
  Sparkles,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function FoundationPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const foundationElements = [
    {
      icon: Rocket,
      title: 'Vision Statement',
      subtitle: "Where You're Headed",
      description:
        "Your vision describes the future state you're working to achieve. It's aspirational, inspiring, and provides long-term direction.",
      link: '/foundation/vision-statement',
      questions: [
        'What will our company look like in 5-10 years?',
        'What impact do we want to have on our industry?',
        'What legacy do we want to create?',
      ],
      examples: [
        'Tesla: "To accelerate the world\'s transition to sustainable energy"',
        'Microsoft: "Empower every person and organization on the planet to achieve more"',
        'Amazon: "To be Earth\'s most customer-centric company"',
      ],
    },
    {
      icon: Target,
      title: 'Mission Statement',
      subtitle: 'Why You Exist',
      description:
        "Your mission defines your company's purpose and core reason for being. It explains what you do, who you serve, and how you do it.",
      link: '/foundation/mission-statement',
      questions: [
        'Why does our company exist?',
        'Who do we serve?',
        'What value do we create?',
        'How are we different?',
      ],
      examples: [
        'Patagonia: "Build the best product, cause no unnecessary harm, use business to inspire solutions to the environmental crisis"',
        'Starbucks: "To inspire and nurture the human spirit – one person, one cup and one neighborhood at a time"',
      ],
    },
    {
      icon: Heart,
      title: 'Core Values',
      subtitle: 'What You Stand For',
      description:
        'Your values are the principles and beliefs that guide decision-making and behavior throughout your organization.',
      link: '/foundation/core-values',
      questions: [
        'What principles are non-negotiable?',
        'How do we want our team to behave?',
        'What do we believe in?',
        'How do we make tough decisions?',
      ],
      examples: [
        'Netflix: Innovation, Courage, Honesty, Selflessness, Inclusion',
        'Google: Focus on the user, Democracy on the web, Fast is better than slow',
      ],
    },
    {
      icon: Lightbulb,
      title: 'Strategic Objectives',
      subtitle: "What You'll Achieve",
      description:
        'Objectives are specific, measurable goals that translate your vision into concrete outcomes. They bridge vision and execution.',
      link: '/foundation/strategic-objectives',
      questions: [
        'What are our top 3-5 priorities?',
        'What must we accomplish this year?',
        "How will we know we're making progress?",
        'What outcomes matter most?',
      ],
      examples: [
        'Achieve $50M in annual revenue by Q4 2027',
        'Expand to 3 new markets within 24 months',
        'Increase customer satisfaction score to 90+',
        'Build a world-class engineering team of 50+ people',
      ],
    },
  ];

  const benefits = [
    'Create clarity on direction and purpose',
    'Align your entire team around what matters',
    'Make better decisions faster',
    'Attract and retain top talent',
    'Build a stronger company culture',
    'Guide resource allocation',
    'Communicate effectively with stakeholders',
    'Drive consistent execution',
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Foundation' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Compass className="w-4 h-4 mr-2" />
            Step 1 of 5
          </span>
        }
        title="Give every decision a clear direction."
        subtitle="Define where your company is going, why it matters and what you stand for. Build a foundation your objectives, people and projects can all work from."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Define my company direction
        </Link>
      </PageHero>

      {/* Foundation Elements */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">
              Start with the decisions that guide everything else.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every successful strategy starts with a strong foundation. These four elements work
              together to create clarity and alignment.
            </p>
          </motion.div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {foundationElements.map((element, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-blue-600 to-purple-600 w-20 h-20 rounded-xl flex items-center justify-center shadow-sm">
                      <element.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold mb-2">{element.title}</h3>
                    <p className="text-blue-600 font-semibold mb-4 text-lg">{element.subtitle}</p>
                    <p className="text-gray-700 mb-6 text-lg">{element.description}</p>

                    {/* Key Questions */}
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Key Questions to Answer:</h4>
                      <ul className="space-y-2">
                        {element.questions.map((question, idx) => (
                          <li key={idx} className="flex items-start">
                            <div className="bg-blue-500 w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0" />
                            <span className="text-gray-700">{question}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Examples */}
                    <div className="public-card bg-white p-4 rounded-lg border border-blue-200 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Real-World Examples:</h4>
                      <ul className="space-y-2">
                        {element.examples.map((example, idx) => (
                          <li key={idx} className="text-sm text-gray-600 italic">
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Learn More Link */}
                    <Link
                      to={element.link}
                      className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                    >
                      Learn More About {element.title} <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* A shared direction makes the next choice clearer. */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                A shared direction makes the next choice clearer.
              </h2>
              <p className="text-xl text-gray-600">
                Without a solid foundation, strategy becomes arbitrary decisions and busy work.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="public-card flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm"
                >
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How StratPlan Helps */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card text-gray-900 p-10 rounded-2xl bg-blue-50"
            >
              <h2 className="text-3xl font-bold mb-6">
                From early ideas to a direction your team can use.
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Sparkles className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Draft with Pro AI</h3>
                    <p className="text-gray-600 text-sm">
                      Explore draft statements based on your company context, then shape them in
                      your own words.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Bring your team in with Pro</h3>
                    <p className="text-gray-600 text-sm">
                      Bring your leadership team into the decisions that will guide everyone else.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Target className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Cover the important questions</h3>
                    <p className="text-gray-600 text-sm">
                      Work through vision, mission, values and objectives in a clear sequence.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">Refine before you move on</h3>
                    <p className="text-gray-600 text-sm">
                      Use practical checklists to review whether your statements are clear and
                      useful.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next Step */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-4">
                Now test your direction against the world outside.
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Once your foundation is solid, move to External Assessment to understand your market
                landscape.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/external-assessment"
                  className="inline-flex items-center justify-center bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold border border-blue-600 hover:bg-blue-50 transition-colors"
                >
                  Next: External Assessment <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link
                  to="/strategic-framework"
                  className="inline-flex items-center justify-center bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  View Full Framework
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="public-section public-cta py-16 bg-blue-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={false} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Start with a direction worth working towards.
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Define the direction you want to pursue, the purpose behind it and the objectives that
              will guide your next decisions.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Build my strategic plan <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
